// v3.18-1383 GMAIL-003 — Full Gmail compose modal.
//
// Sends via the connected Google mailbox (POST /api/gmail/compose/send)
// — NOT via SMTP2GO. SMTP2GO remains authoritative for automated system
// email exactly as designed.
//
// Supports:
//   • New Email / Reply / Reply All / Forward (thread + In-Reply-To
//     preserved for perfect Gmail conversation threading)
//   • CC / BCC toggle
//   • Rich text editor (bold, italic, underline, link, lists, quote)
//   • File attachments (drag-drop + picker, base64 payload to backend)
//   • Draft save (Gmail Drafts folder)
//   • Send immediately
//   • From selector when multiple mailboxes are connected
//   • Signature block (auto-appended, editable)
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  X, Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon,
  Paperclip, Send, Save, Loader2, ChevronDown, Quote, FileText,
} from 'lucide-react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import EmailTemplatePickerDialog from './EmailTemplatePickerDialog';

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = () => reject(r.error);
    r.onload = () => {
      const s = String(r.result || '');
      // strip "data:*/*;base64,"
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    r.readAsDataURL(file);
  });

const parseAddresses = (s) =>
  (s || '')
    .split(/[,;\n]/)
    .map((x) => x.trim())
    .filter(Boolean);

const buildQuotedReply = (msg) => {
  if (!msg) return '';
  const when = msg.internal_date ? new Date(msg.internal_date).toLocaleString() : '';
  const header = `On ${when}, ${msg.from || ''} wrote:`;
  const body = msg.body_html || (msg.body_text || '').replace(/\n/g, '<br/>');
  return (
    `<br/><br/><div style="border-left:3px solid #cbd5e1;padding:6px 12px;color:#475569;font-size:13px;">` +
    `<div style="font-weight:600;margin-bottom:4px;">${header}</div>${body}</div>`
  );
};

/**
 * mode: 'new' | 'reply' | 'replyAll' | 'forward'
 * sourceMessage: original message (for reply/forward). Must include
 *   thread_id, gmail_message_id, message_id_header, from, to, cc,
 *   subject, body_html/body_text, attachments.
 * initialTo: array of pre-filled recipient addresses (for 'new').
 * clientId: optional client to attach the sent email to.
 */
export default function GmailComposeModal({
  open, onClose, mailboxes, defaultMailboxId,
  mode = 'new', sourceMessage = null,
  initialTo = [], initialSubject = '', initialBodyHtml = '',
  clientId = null,
  onSent,
}) {
  const [mailboxId, setMailboxId] = useState(defaultMailboxId || '');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [signature, setSignature] = useState(
    () => localStorage.getItem('gmail_compose_signature') ||
      '--\nSent via Prestigious Hub',
  );
  const [sending, setSending] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const editorRef = useRef(null);
  const fileRef = useRef(null);
  const [templateOpen, setTemplateOpen] = useState(false);

  const selectedMailbox = useMemo(
    () => (mailboxes || []).find((m) => m.id === mailboxId) || null,
    [mailboxes, mailboxId],
  );

  // ---- Prefill on open (mode-aware) ------------------------------------
  useEffect(() => {
    if (!open) return;
    setMailboxId(defaultMailboxId || (mailboxes?.[0]?.id || ''));
    setAttachments([]);
    setShowCc(false);
    setShowBcc(false);

    if (mode === 'new') {
      setTo(initialTo.join(', '));
      setCc('');
      setBcc('');
      setSubject(initialSubject);
      queueMicrotask(() => {
        if (editorRef.current) editorRef.current.innerHTML = initialBodyHtml || '';
      });
      return;
    }

    const src = sourceMessage || {};
    const originalFrom = src.from || '';
    const originalTo = (src.to || '').split(',').map((s) => s.trim()).filter(Boolean);
    const originalCc = (src.cc || '').split(',').map((s) => s.trim()).filter(Boolean);
    const myEmail = (selectedMailbox?.email_address || '').toLowerCase();

    if (mode === 'reply') {
      setTo(originalFrom);
      setCc('');
      setSubject(src.subject?.startsWith('Re:') ? src.subject : `Re: ${src.subject || ''}`);
    } else if (mode === 'replyAll') {
      setTo(originalFrom);
      // include original recipients minus us
      const others = [...originalTo, ...originalCc]
        .filter((a) => a && !a.toLowerCase().includes(myEmail));
      setCc(others.join(', '));
      if (others.length) setShowCc(true);
      setSubject(src.subject?.startsWith('Re:') ? src.subject : `Re: ${src.subject || ''}`);
    } else if (mode === 'forward') {
      setTo('');
      setCc('');
      setSubject(src.subject?.startsWith('Fwd:') ? src.subject : `Fwd: ${src.subject || ''}`);
    }
    queueMicrotask(() => {
      if (editorRef.current) editorRef.current.innerHTML = buildQuotedReply(src);
    });
  }, [open, mode]);

  // ---- Rich text toolbar ----------------------------------------------
  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    editorRef.current?.focus();
  };
  const insertLink = () => {
    const url = window.prompt('Enter URL:');
    if (url) exec('createLink', url);
  };

  // ---- Attachment handling --------------------------------------------
  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    const encoded = await Promise.all(
      files.map(async (f) => ({
        filename: f.name,
        mimeType: f.type || 'application/octet-stream',
        size: f.size,
        data_b64: await readFileAsBase64(f),
      })),
    );
    setAttachments((prev) => [...prev, ...encoded]);
  };
  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const removeAtt = (idx) =>
    setAttachments((prev) => prev.filter((_, i) => i !== idx));

  // ---- Send / Draft ----------------------------------------------------
  const buildPayload = (save_as_draft) => {
    const html = editorRef.current?.innerHTML || '';
    const withSig =
      signature?.trim()
        ? html + `<br/><br/><div style="color:#64748b;font-size:12px;white-space:pre-wrap;">${signature}</div>`
        : html;
    const src = sourceMessage || {};
    return {
      client_id:     clientId || null,
      mailbox_id:    mailboxId || null,
      to:            parseAddresses(to),
      cc:            showCc ? parseAddresses(cc) : null,
      bcc:           showBcc ? parseAddresses(bcc) : null,
      subject,
      body_html:     withSig,
      body_text:     (editorRef.current?.innerText || '') +
                     (signature ? '\n\n' + signature : ''),
      thread_id:     mode !== 'new' ? (src.thread_id || null) : null,
      in_reply_to:   mode !== 'new' ? (src.message_id_header || null) : null,
      references:    mode !== 'new' ? (src.references_header || src.message_id_header || null) : null,
      save_as_draft,
      attachments,
    };
  };

  const doSend = async () => {
    if (!parseAddresses(to).length) {
      toast.error('Please add at least one recipient'); return;
    }
    setSending(true);
    try {
      const { data } = await api.post('/gmail/compose/send', buildPayload(false));
      if (data?.status === 'needs_connect') {
        toast.error(`Mailbox not connected: ${data.target_email || ''}`);
        return;
      }
      toast.success('Email sent via ' + (data?.from || 'Gmail'));
      localStorage.setItem('gmail_compose_signature', signature || '');
      onSent?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send email');
    } finally { setSending(false); }
  };

  const doSaveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post('/gmail/compose/send', buildPayload(true));
      toast.success('Draft saved to Gmail');
      localStorage.setItem('gmail_compose_signature', signature || '');
      onSent?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save draft');
    } finally { setSavingDraft(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3"
      data-testid="gmail-compose-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onDragOver={(e) => e.preventDefault()}
      onDrop={onDrop}
    >
      <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-900 text-white">
          <div className="text-sm font-bold" data-testid="gmail-compose-title">
            {mode === 'reply' ? 'Reply' :
              mode === 'replyAll' ? 'Reply All' :
                mode === 'forward' ? 'Forward' : 'New message'}
          </div>
          <button
            onClick={onClose}
            className="text-slate-300 hover:text-white"
            data-testid="gmail-compose-close"
          ><X className="w-4 h-4" /></button>
        </div>

        {/* From */}
        <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center gap-2">
          <span className="text-xs font-semibold text-slate-500 w-14">From</span>
          <div className="relative flex-1">
            <select
              data-testid="gmail-compose-from"
              value={mailboxId}
              onChange={(e) => setMailboxId(e.target.value)}
              className="w-full appearance-none pl-3 pr-8 py-1.5 rounded-lg border border-slate-300 text-sm font-mono bg-white"
            >
              {(mailboxes || []).map((m) => (
                <option key={m.id} value={m.id}>{m.email_address}</option>
              ))}
            </select>
            <ChevronDown className="w-3.5 h-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* To / CC / BCC */}
        <div className="px-4 py-2 border-b border-slate-100 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500 w-14">To</span>
            <Input
              data-testid="gmail-compose-to"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="recipient@example.com"
              className="flex-1 h-8 text-sm"
            />
            <button
              onClick={() => setShowCc((v) => !v)}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
              data-testid="gmail-compose-toggle-cc"
            >Cc</button>
            <button
              onClick={() => setShowBcc((v) => !v)}
              className="text-[11px] font-semibold text-slate-500 hover:text-slate-800"
              data-testid="gmail-compose-toggle-bcc"
            >Bcc</button>
          </div>
          {showCc && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 w-14">Cc</span>
              <Input
                data-testid="gmail-compose-cc"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                className="flex-1 h-8 text-sm"
              />
            </div>
          )}
          {showBcc && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-slate-500 w-14">Bcc</span>
              <Input
                data-testid="gmail-compose-bcc"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="bcc@example.com"
                className="flex-1 h-8 text-sm"
              />
            </div>
          )}
        </div>

        {/* Subject */}
        <div className="px-4 py-2 border-b border-slate-100">
          <Input
            data-testid="gmail-compose-subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Subject"
            className="h-8 text-sm border-0 shadow-none focus-visible:ring-0 px-0 font-semibold"
          />
        </div>

        {/* Rich text toolbar */}
        <div className="px-2 py-1 border-b border-slate-100 flex items-center gap-1 bg-slate-50" data-testid="gmail-compose-toolbar">
          {[
            { cmd: 'bold', Icon: Bold, tid: 'bold' },
            { cmd: 'italic', Icon: Italic, tid: 'italic' },
            { cmd: 'underline', Icon: Underline, tid: 'underline' },
            { cmd: 'insertUnorderedList', Icon: List, tid: 'ul' },
            { cmd: 'insertOrderedList', Icon: ListOrdered, tid: 'ol' },
            { cmd: 'formatBlock', val: 'blockquote', Icon: Quote, tid: 'quote' },
          ].map(({ cmd, val, Icon, tid }) => (
            <button
              key={tid}
              type="button"
              data-testid={`gmail-compose-fmt-${tid}`}
              onClick={() => exec(cmd, val)}
              className="p-1.5 rounded hover:bg-slate-200 text-slate-700"
            ><Icon className="w-3.5 h-3.5" /></button>
          ))}
          <button
            type="button"
            onClick={insertLink}
            data-testid="gmail-compose-fmt-link"
            className="p-1.5 rounded hover:bg-slate-200 text-slate-700"
          ><LinkIcon className="w-3.5 h-3.5" /></button>
        </div>

        {/* Editor + attachments */}
        <div className="flex-1 overflow-y-auto">
          <div
            ref={editorRef}
            contentEditable
            suppressContentEditableWarning
            data-testid="gmail-compose-editor"
            className="p-4 min-h-[200px] text-sm text-slate-800 focus:outline-none prose prose-sm max-w-none"
          />
          {attachments.length > 0 && (
            <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="gmail-compose-attachments">
              {attachments.map((a, i) => (
                <div
                  key={`${a.filename}-${i}`}
                  className="inline-flex items-center gap-2 px-2 py-1 rounded-lg bg-slate-100 border border-slate-200 text-xs text-slate-700"
                  data-testid={`gmail-compose-att-${i}`}
                >
                  <Paperclip className="w-3 h-3" />
                  <span className="max-w-[220px] truncate">{a.filename}</span>
                  <span className="text-slate-400">
                    ({Math.max(1, Math.round((a.size || 0) / 1024))}KB)
                  </span>
                  <button
                    onClick={() => removeAtt(i)}
                    className="text-slate-500 hover:text-red-600"
                  ><X className="w-3 h-3" /></button>
                </div>
              ))}
            </div>
          )}
          {/* Editable signature */}
          <div className="px-4 pb-4 border-t border-slate-100 pt-2">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Signature (auto-appended)</div>
            <textarea
              data-testid="gmail-compose-signature"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              rows={2}
              className="w-full text-xs text-slate-600 border border-slate-200 rounded p-2 focus:outline-none focus:ring-1 focus:ring-slate-400"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-100 flex items-center gap-2 bg-slate-50">
          <input
            ref={fileRef}
            type="file"
            multiple
            className="hidden"
            data-testid="gmail-compose-file-input"
            onChange={(e) => addFiles(e.target.files)}
          />
          <Button
            variant="outline"
            size="sm"
            data-testid="gmail-compose-attach"
            onClick={() => fileRef.current?.click()}
            className="text-xs"
          >
            <Paperclip className="w-3.5 h-3.5 mr-1" />
            Attach
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="gmail-compose-template"
            onClick={() => setTemplateOpen(true)}
            className="text-xs"
          >
            <FileText className="w-3.5 h-3.5 mr-1" />
            Template
          </Button>
          <Button
            variant="outline"
            size="sm"
            data-testid="gmail-compose-save-draft"
            onClick={doSaveDraft}
            disabled={savingDraft || sending}
            className="text-xs"
          >
            {savingDraft ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> :
              <Save className="w-3.5 h-3.5 mr-1" />}
            Save draft
          </Button>
          <div className="flex-1" />
          <Button
            size="sm"
            data-testid="gmail-compose-send"
            onClick={doSend}
            disabled={sending || savingDraft || !mailboxId}
            className="bg-slate-900 hover:bg-black text-white text-xs"
          >
            {sending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> :
              <Send className="w-3.5 h-3.5 mr-1" />}
            Send
          </Button>
        </div>
      </div>
      {/* Template picker */}
      <EmailTemplatePickerDialog
        open={templateOpen}
        onOpenChange={setTemplateOpen}
        clientId={clientId || null}
        onSelect={(payload) => {
          if (payload?.rendered) {
            if (payload.rendered.subject && !subject) setSubject(payload.rendered.subject);
            if (payload.rendered.body_html && editorRef.current) {
              editorRef.current.innerHTML =
                (editorRef.current.innerHTML || '') + payload.rendered.body_html;
            }
            toast.success(`Inserted template: ${payload.template?.name || 'template'}`);
          }
          setTemplateOpen(false);
        }}
      />
    </div>
  );
}
